import humanizeDuration from 'humanize-duration';

import { SECONDS_PER_DAY, SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from './date';
import { Prettify } from './type';

type TimeUnit = 'ms' | 's' | 'm' | 'h' | 'd';

/**
 * A collection of fixed information about a time unit.
 */
type TimeUnitInfo = {
  /**
   * The number to multiply to convert from seconds to the target unit
   */
  secondsScalar: number;
  /**
   * The shortened code for the unit
   *
   * @example
   * 's'
   */
  unit: TimeUnit;
  /**
   * The singular name of the unit
   *
   * @example
   * 'second'
   */
  unitName: string;
  /**
   * The pluralized name of the unit
   *
   * @example
   * 'seconds'
   */
  unitNamePlural: string;
  /**
   * The abbreviated pluralized name of the unit
   *
   * @example
   * 'secs'
   */
  unitsAbbrev: string;
};

const UNIT_INFO: Record<TimeUnit, TimeUnitInfo> = {
  ms: {
    secondsScalar: 60000,
    unit: 'ms',
    unitName: 'millisecond',
    unitNamePlural: 'milliseconds',
    unitsAbbrev: 'millis',
  },
  s: {
    secondsScalar: 60,
    unit: 's',
    unitName: 'second',
    unitNamePlural: 'seconds',
    unitsAbbrev: 'secs',
  },
  m: {
    secondsScalar: 1,
    unit: 'm',
    unitName: 'minute',
    unitNamePlural: 'minutes',
    unitsAbbrev: 'mins',
  },
  h: {
    secondsScalar: 1 / 60,
    unit: 'h',
    unitName: 'hour',
    unitNamePlural: 'hours',
    unitsAbbrev: 'hrs',
  },
  d: {
    secondsScalar: 1 / (24 * 60),
    unit: 'd',
    unitName: 'day',
    unitNamePlural: 'days',
    unitsAbbrev: 'days',
  },
};

function getReasonableUnit(sourceUnit: TimeUnit, duration: number) {
  duration = Math.abs(convertDuration(sourceUnit, duration, 's'));
  if (duration === 0) {
    return UNIT_INFO.s;
  } else if (duration < 1) {
    return UNIT_INFO.ms;
  } else if (duration < SECONDS_PER_MINUTE) {
    return UNIT_INFO.s;
  } else if (duration < SECONDS_PER_HOUR) {
    return UNIT_INFO.m;
  } else if (duration < SECONDS_PER_DAY) {
    return UNIT_INFO.h;
  }
  return UNIT_INFO.d;
}

/**
 * Convert from one time unit to another.
 * @param duration The duration to convert
 * @param sourceUnit The unit of the duration
 * @param targetUnit The unit to convert to
 *
 * @example
 * // returns 2
 * convertDuration('ms', 2000, 's');
 */
export const convertDuration = (
  sourceUnit: TimeUnit,
  duration: number,
  targetUnit: TimeUnit,
) => {
  const sourceUnitInfo = UNIT_INFO[sourceUnit];
  const targetUnitInfo = UNIT_INFO[targetUnit];
  return (duration * targetUnitInfo.secondsScalar) / sourceUnitInfo.secondsScalar;
};

type DurationFormatter = Prettify<
  TimeUnitInfo & {
    /**
     * @param sourceUnits The input duration unit type
     * @param duration An input duration of units `units`
     * @returns converts duration to the determined max unit scale
     *
     * @example
     * // formatDuration will determine that the max unit scale is seconds
     * const formatDuration = generateDurationFormatter('ms', 1000);
     * // returns 2 (convert ms of 2000 to max units (s))
     * formatDuration.toMaxUnits('ms', 2000);
     */
    toMaxUnits: (sourceUnits: TimeUnit, duration: number) => number;
    /**
     * @param duration An input duration of the human readible unit type
     * @param targetUnits The target duration unit type
     * @returns converts duration to the target duration unit type from the max unit scale
     *
     * @example
     * // formatDuration will determine that the max unit scale is seconds
     * const formatDuration = generateDurationFormatter('ms', 1000);
     * // returns 2000 (convert max units (s) of 2 to ms
     * formatDuration.fromMaxUnits(2, 'ms');
     */
    fromMaxUnits: (duration: number, targetUnit: TimeUnit) => number;
    /**
     * Turn any duration into a human-readable string.
     *
     * @param sourceUnits The input duration unit type
     * @param duration An input duration of units `units`
     * @param options {@link humanizeDuration.Options} to pass to humanizeDuration
     *
     * @example
     * // formatDuration will determine that the max unit scale is minutes
     * const formatDuration = generateDurationFormatter('ms', 60000);
     * // returns '3 minutes, 20 seconds'
     * formatDuration.humanize('s', 200);
     */
    humanize: (
      sourceUnits: TimeUnit | 'MAX',
      duration: number,
      options?: humanizeDuration.Options,
    ) => string;
    /**
     * Display the maximum value which was passed in to build the formatter.
     *
     * @param options {@link humanizeDuration.Options} to pass to humanizeDuration
     *
     * @example
     * // formatDuration will determine that the max unit scale is minutes
     * const formatDuration = generateDurationFormatter('ms', 60000);
     * // returns '60 seconds'
     * formatDuration.humanizeMax();
     */
    humanizeMax: (options?: humanizeDuration.Options) => string;
  }
>;

/**
 * Given a max number of duration of type sourceUnit, return a formatter for
 * turning other durations into a human-readable time scale. This ensures that
 * we use a consistent scale (ms, s, m, h, d) for time.
 *
 * @example
 * // formatDuration will determine that the max unit scale is minutes since its >= 60 seconds
 * const formatDuration = generateDurationFormatter('s', 65);
 * // returns '1 minute, 5 seconds'
 * formatDuration.humanizeMax();
 * // returns '1 minute, 12 seconds'
 * formatDuration.humanize('s', 72);
 * // returns '72 milliseconds'
 * formatDuration.humanize('ms', 72);
 */
export function generateDurationFormatter(
  /** The unit of the `maximumDuration` */
  sourceUnit: TimeUnit,
  /** The maximum value you want to include in the time scale */
  maxDuration: number | null,
  /** If unspecified, we will attempt to find the most reasonable target unit */
  targetUnit?: TimeUnit,
): DurationFormatter {
  const reasonableMaxUnitsInfo = targetUnit
    ? UNIT_INFO[targetUnit]
    : getReasonableUnit(sourceUnit, maxDuration || 0);
  if (maxDuration == null) {
    maxDuration = 0;
  }

  const humanize = (sourceUnits: TimeUnit | 'MAX', duration: number) => {
    const durationMillis = convertDuration(
      sourceUnits === 'MAX' ? reasonableMaxUnitsInfo.unit : sourceUnits,
      duration ?? maxDuration ?? 0,
      'ms',
    );
    const isMilliseconds = durationMillis < 1000;
    const isSeconds = !isMilliseconds && durationMillis < 60 * 1000;
    return humanizeDuration(durationMillis, {
      maxDecimalPoints: isSeconds ? 3 : 0,
      largest: 2,
      units: isMilliseconds ? ['ms'] : ['d', 'h', 'm', 's'],
    });
  };

  return {
    ...reasonableMaxUnitsInfo,
    toMaxUnits: (sourceUnits: TimeUnit, duration: number) =>
      convertDuration(sourceUnits, duration, reasonableMaxUnitsInfo.unit),
    fromMaxUnits: (duration: number, targetUnit: TimeUnit) =>
      convertDuration(reasonableMaxUnitsInfo.unit, duration, targetUnit),
    humanize,
    humanizeMax: () => humanize('MAX', maxDuration ?? 0),
  };
}
